import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CaretLeft, CaretRight, Info } from '@phosphor-icons/react';
import { createPortal } from 'react-dom';
import { useTranslation } from '@client-app/i18n';
import { billingService, MarketUsageAmount, MarketUsageCharge, MarketUsageMonitor,
  MarketUsagePage, MarketUsagePeriod, MarketUsageQuery, MarketUsageRun } from '@common/api/billingService';
import { formatUsageEventTime } from './usageDisplayUtils';

const channels: Record<string, string> = {
  google_search: 'Google Search', google_trends: 'Google Trends', meta: 'Meta Ads', reddit: 'Reddit',
  youtube: 'YouTube', amazon: 'Amazon', facebook: 'Facebook', instagram: 'Instagram', x: 'X', tiktok: 'TikTok Ads',
};

// Never convert ledger integers to floating point, including values beyond Number.MAX_SAFE_INTEGER.
export function formatMarketUsage(value: string | null, showSign = true): string {
  if (value == null || !/^-?\d+$/.test(value)) return '—';
  const cost = BigInt(value); const negative = cost > BigInt(0);
  const absolute = cost < BigInt(0) ? -cost : cost;
  if (absolute > BigInt(0) && absolute < BigInt(1000)) return `${showSign ? (negative ? '−' : '+') : ''}<0.01`;
  const amount = (absolute + BigInt(500)) / BigInt(1000);
  const fraction = (amount % BigInt(100)).toString().padStart(2, '0');
  const sign = showSign ? (negative ? '−' : cost < BigInt(0) ? '+' : '') : '';
  return `${sign}${(amount / BigInt(100)).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')}.${fraction}`;
}

type Load<T> = { status: 'loading' } | { status: 'error' } | { status: 'ready'; data: MarketUsagePage<T> };
function useUsagePage<T extends MarketUsageAmount>(query: MarketUsageQuery) {
  const [state, setState] = useState<Load<T>>({ status: 'loading' });
  const [retry, setRetry] = useState(0);
  const { window_id, monitor_id, run_id, page = 1 } = query;
  useEffect(() => {
    let active = true;
    setState({ status: 'loading' });
    billingService.getMarketUsageTree<T>({ window_id, monitor_id, run_id, page, size: 10 }).then(data => {
      if (!active) return;
      if (!data || !Array.isArray(data.items) || typeof data.total !== 'number') {
        setState({ status: 'error' }); return;
      }
      setState({ status: 'ready', data });
    }).catch(() => { if (active) setState({ status: 'error' }); });
    return () => { active = false; };
  }, [window_id, monitor_id, run_id, page, retry]);
  return { state, retry: () => setRetry(value => value + 1) };
}

const tableGridStyle = { gridTemplateColumns: 'minmax(0, 0.88fr) 190px 205px 156px' };
const rowClass = 'grid items-center gap-0 px-6 py-4';
const detailRowClass = 'grid items-center gap-0 px-6 py-3.5';
const muted = 'mt-1 text-xs text-[#666F8D]';
const buttonClass = 'rounded px-2 py-1 text-sm text-[#666F8D] hover:bg-[#F4F5F8] focus-visible:outline focus-visible:outline-2 disabled:opacity-40';
const PAGE_SIZE = 10;
const MAX_VISIBLE_PAGE_BUTTONS = 5;

function DisclosureIcon({ open }: { open: boolean }) {
  return <CaretRight aria-hidden="true" size={14} weight="bold"
    className={`shrink-0 text-[#8A93B2] transition-transform duration-200 ${open ? 'rotate-90' : ''}`} />;
}

function Time({ value, muted = false }: { value: string | null; muted?: boolean }) {
  const { locale } = useTranslation();
  const { localTime, timezone } = formatUsageEventTime(value, locale);
  if (!timezone) return <span>—</span>;
  return <time dateTime={value || undefined}>
    <span className={`block whitespace-nowrap font-normal leading-5 ${muted ? 'text-[13px] text-[#666F8D]' : 'text-sm text-[#1A162F]'}`}>{localTime}</span>
    <span className="mt-0.5 block text-xs font-normal leading-4 text-[#666F8D]">{timezone}</span>
  </time>;
}
function Amount({ item, pending = false, showSign = true, className }: {
  item: MarketUsageAmount;
  pending?: boolean;
  showSign?: boolean;
  className?: string;
}) {
  const { t } = useTranslation();
  if (pending && (item.amount_unknown || item.period_subunits == null || item.period_subunits === '0')) {
    return <span className={className || 'text-sm font-medium text-[#1A162F]'}>—</span>;
  }
  if (item.amount_unknown || item.period_subunits == null) {
    return <span className={className || 'text-sm font-medium text-[#1A162F]'}>{t('website.usage.tree.amountPending')}</span>;
  }
  return <span className={className || "text-sm font-medium tabular-nums text-[#1A162F]"}>
    {formatMarketUsage(item.period_subunits, showSign)}
  </span>;
}
function LoadState<T>({ state, retry }: { state: Load<T>; retry: () => void }) {
  const { t } = useTranslation();
  if (state.status === 'loading') return <div role="status" className="p-6 text-sm text-[#666F8D]">{t('website.usage.tree.loading')}</div>;
  if (state.status === 'error') return <div role="alert" className="p-6 text-sm">
    {t('website.usage.recordsLoadError')} <button type="button" className={buttonClass} onClick={retry}>{t('website.usage.retryRecords')}</button>
  </div>;
  if (state.data.period && !state.data.items.length) return <p className="p-6 text-sm text-[#666F8D]">{t('website.usage.noMarketUsage')}</p>;
  return null;
}
function Pages({ page, total, setPage }: { page: number; total: number; setPage: (page: number) => void }) {
  const { t } = useTranslation();
  if (total <= 10) return null;
  return <div className="flex items-center justify-end gap-3 border-t border-[#ECEFF5] px-6 py-3">
    <button type="button" className={buttonClass} disabled={page === 1} onClick={() => setPage(page - 1)}>{t('website.usage.tree.previous')}</button>
    <span className="text-xs">{page} / {Math.ceil(total / 10)}</span>
    <button type="button" className={buttonClass} disabled={page * 10 >= total} onClick={() => setPage(page + 1)}>{t('website.usage.tree.next')}</button>
  </div>;
}
function TablePages({ page, total, setPage }: { page: number; total: number; setPage: (page: number) => void }) {
  const { t, locale } = useTranslation();
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const half = Math.floor(MAX_VISIBLE_PAGE_BUTTONS / 2);
  let startPage = Math.max(1, page - half);
  let endPage = Math.min(totalPages, startPage + MAX_VISIBLE_PAGE_BUTTONS - 1);
  if (endPage - startPage + 1 < MAX_VISIBLE_PAGE_BUTTONS) startPage = Math.max(1, endPage - MAX_VISIBLE_PAGE_BUTTONS + 1);
  const visiblePages = Array.from({ length: endPage - startPage + 1 }, (_, index) => startPage + index);
  const formatInteger = (value: number) => new Intl.NumberFormat(locale.startsWith('zh') ? 'zh-CN' : 'en-US').format(value);
  return <div className="flex items-center justify-between border-t border-[#ECEFF5] bg-[#FCFCFC] px-6 py-4">
    <span className="text-sm font-normal text-[#666F8D]">
      {total > 0 ? t('website.usage.showingEventsRange', {
        start: formatInteger((page - 1) * PAGE_SIZE + 1),
        end: formatInteger(Math.min(page * PAGE_SIZE, total)),
        total: formatInteger(total),
      }) : t('website.usage.showingZeroEvents', { total: formatInteger(0) })}
    </span>
    <div className="flex items-center gap-2">
      <button type="button" onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1}
        className="rounded-lg p-2 text-[#8A93B2] transition-colors hover:bg-[#F4F5F8] hover:text-[#1A162F] disabled:cursor-not-allowed disabled:opacity-50">
        <CaretLeft aria-hidden="true" size={16} weight="regular" />
      </button>
      <div className="flex items-center gap-1">
        {visiblePages.map(visiblePage => <button type="button" key={visiblePage} onClick={() => setPage(visiblePage)}
          className={`h-8 w-8 rounded-lg text-sm font-medium transition-colors ${page === visiblePage
            ? 'bg-[#1A162F] text-white hover:bg-primary/90'
            : 'text-[#666F8D] hover:bg-[#F4F5F8] hover:text-[#1A162F]'}`}>
          {visiblePage}
        </button>)}
      </div>
      <button type="button" onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page === totalPages}
        className="rounded-lg p-2 text-[#8A93B2] transition-colors hover:bg-[#F4F5F8] hover:text-[#1A162F] disabled:cursor-not-allowed disabled:opacity-50">
        <CaretRight aria-hidden="true" size={16} weight="regular" />
      </button>
    </div>
  </div>;
}
function RunBreakdownHover({
  monitor,
  run,
  period,
  label,
}: {
  monitor: string;
  run: string;
  period: MarketUsagePeriod;
  label: string;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ left: number; top: number; above: boolean; listHeight: number } | null>(null);
  const [chargesState, setChargesState] = useState<{
    status: 'idle' | 'loading' | 'ready' | 'error';
    data?: MarketUsagePage<MarketUsageCharge>;
  }>({ status: 'idle' });
  const closeTimerRef = useRef<number | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  const fetchCharges = useCallback(() => {
    if (chargesState.status === 'ready' || chargesState.status === 'loading') return;
    setChargesState({ status: 'loading' });
    billingService
      .getMarketUsageTree<MarketUsageCharge>({
        window_id: period.id,
        monitor_id: monitor,
        run_id: run,
        page: 1,
        size: 20,
      })
      .then(data => {
        if (!data || !Array.isArray(data.items)) {
          setChargesState({ status: 'error' });
          return;
        }
        setChargesState({ status: 'ready', data });
      })
      .catch(() => setChargesState({ status: 'error' }));
  }, [chargesState.status, monitor, run, period.id]);

  const cancelHide = () => {
    if (closeTimerRef.current !== null && typeof window !== 'undefined') {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const show = (target: HTMLElement) => {
    cancelHide();
    const rect = target.getBoundingClientRect ? target.getBoundingClientRect() : { left: 16, top: 20, bottom: 40 };
    const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 768;
    const viewportWidth = typeof window !== 'undefined' ? (window.innerWidth ?? rect.left + 296) : rect.left + 296;
    const aboveSpace = (rect.top ?? rect.bottom) - 16;
    const belowSpace = viewportHeight - rect.bottom - 16;
    const above = belowSpace < 290 && aboveSpace > belowSpace;
    const availableSpace = above ? aboveSpace : belowSpace;
    setCoords({
      left: Math.min(Math.max(16, rect.left), Math.max(16, viewportWidth - 296)),
      top: above ? (rect.top ?? rect.bottom) - 6 : rect.bottom + 6,
      above,
      listHeight: Math.max(48, Math.min(220, availableSpace - 70)),
    });
    setOpen(true);
    fetchCharges();
  };

  const hideWithDelay = () => {
    cancelHide();
    if (typeof window === 'undefined') {
      setOpen(false);
      return;
    }
    closeTimerRef.current = window.setTimeout(() => {
      closeTimerRef.current = null;
      setOpen(false);
    }, 150);
  };

  useEffect(() => {
    if (!open || typeof window === 'undefined') return;
    const hideOnOutsideScroll = (event: Event) => {
      if (cardRef.current?.contains(event.target as Node)) return;
      setOpen(false);
    };
    const hide = () => setOpen(false);
    window.addEventListener('scroll', hideOnOutsideScroll, true);
    window.addEventListener('resize', hide);
    return () => {
      window.removeEventListener('scroll', hideOnOutsideScroll, true);
      window.removeEventListener('resize', hide);
    };
  }, [open]);

  useEffect(() => {
    return () => {
      if (closeTimerRef.current !== null && typeof window !== 'undefined') window.clearTimeout(closeTimerRef.current);
    };
  }, []);

  const cardContent = open && coords && (
    <div
      ref={cardRef}
      role="tooltip"
      aria-label={t('website.usage.tree.details')}
      className="fixed z-[30000] w-[280px] rounded-xl border border-[#E8EBF3] bg-white p-3.5 text-xs text-[#1A162F] shadow-[0_10px_24px_rgba(26,22,47,0.12)]"
      style={{
        left: coords.left,
        top: coords.top,
        transform: coords.above ? 'translateY(-100%)' : undefined,
      }}
      onMouseEnter={cancelHide}
      onMouseLeave={hideWithDelay}
    >
      <div className="mb-2.5 flex items-center justify-between border-b border-[#ECEFF5] pb-2">
        <span className="font-medium text-[#1A162F]">{t('website.usage.tree.details')}</span>
        {chargesState.status === 'ready' && chargesState.data && (
          <span className="text-[11px] text-[#8A93B2]">
            {chargesState.data.total}
          </span>
        )}
      </div>
      {chargesState.status === 'loading' && (
        <div className="py-4 text-center text-xs text-[#8A93B2]">
          {t('website.usage.tree.loading')}
        </div>
      )}
      {chargesState.status === 'error' && (
        <div className="py-3 text-center text-xs text-[#E11D48]">
          {t('website.usage.recordsLoadError')}
        </div>
      )}
      {chargesState.status === 'ready' && chargesState.data && (
        chargesState.data.items.length === 0 ? (
          <div className="py-3 text-center text-xs text-[#8A93B2]">
            {t('website.usage.tree.noCharges')}
          </div>
        ) : (
          <div className="custom-scrollbar space-y-2 overflow-y-auto overscroll-contain pr-1" style={{ maxHeight: coords.listHeight }} tabIndex={0} aria-label={t('website.usage.tree.details')}>
            {chargesState.data.items.map(charge => (
              <div key={charge.id} className="flex items-center justify-between gap-3">
                <span className="truncate text-[#666F8D]">
                  {charge.stage === 'analysis'
                    ? t('website.usage.monitorAnalysis')
                    : `${channels[charge.channel || ''] || t('website.usage.channelNotRecorded')} · ${t('website.usage.monitorCollection')}`}
                </span>
                <span className="font-medium tabular-nums text-[#1A162F] shrink-0">
                  {formatMarketUsage(charge.period_subunits, false)}
                </span>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );

  return (
    <>
      <div
        className="inline-flex items-center gap-1.5"
        onMouseEnter={e => show(e.currentTarget)}
        onMouseLeave={hideWithDelay}
      >
        <span className="text-[13px] font-normal text-[#475467] transition-colors hover:text-[#4F46E5] cursor-default">
          {label}
        </span>
        <button
          type="button"
          aria-label={t('website.usage.tree.details')}
          className="rounded p-0.5 text-[#8A93B2] transition-colors hover:text-[#1A162F] focus-visible:outline focus-visible:outline-2"
          onClick={e => show(e.currentTarget)}
        >
          <Info aria-hidden="true" size={14} weight="regular" />
        </button>
      </div>
      {/* Keep portal hover events outside the trigger's React ancestry. */}
      {open && cardContent && (
        typeof document !== 'undefined' && document?.body
          ? createPortal(cardContent, document.body)
          : cardContent
      )}
    </>
  );
}

function RunRow({ item, monitor, period }: { item: MarketUsageRun; monitor: string; period: MarketUsagePeriod }) {
  const { t } = useTranslation();
  const label = item.kind === 'unknown' ? 'historical' : item.kind === 'analysis' ? 'analysis' : 'update';
  const showRunTotal = !item.total_amount_unknown
    && formatMarketUsage(item.total_subunits) !== formatMarketUsage(item.period_subunits);
  return <div className="border-b border-[#E8EBF3] last:border-b-0">
    <div className={detailRowClass} style={tableGridStyle}>
      <div className="min-w-0 pl-11 pr-3 flex items-center">
        <RunBreakdownHover
          monitor={monitor}
          run={item.id}
          period={period}
          label={t(`website.usage.tree.${label}`)}
        />
      </div>
      <div className="pl-5">{item.kind === 'unknown' ? '—' : <Time value={item.started_at} muted />}</div>
      <div aria-hidden="true" />
      <div className="justify-self-start">
        <Amount item={item} pending={item.billing_status === 'pending'} className="text-[13px] font-normal tabular-nums text-[#666F8D]" />
        {showRunTotal && <div className={muted}>
          {t('website.usage.tree.runTotal')} {formatMarketUsage(item.total_subunits)}
        </div>}
      </div>
    </div>
  </div>;
}
function Runs({ monitor, period }: { monitor: string; period: MarketUsagePeriod }) {
  const [page, setPage] = useState(1);
  const { state, retry } = useUsagePage<MarketUsageRun>({ window_id: period.id, monitor_id: monitor, page });
  return <div className="border-t border-[#E8EBF3] bg-[#F8F9FC]"><LoadState state={state} retry={retry} />{state.status === 'ready' && <>
    {state.data.items.map(item => <RunRow key={`${page}:${item.id}`} item={item} monitor={monitor} period={period} />)}
    <Pages page={page} total={state.data.total} setPage={setPage} />
  </>}</div>;
}
function MonitorRow({ item, period }: { item: MarketUsageMonitor; period: MarketUsagePeriod }) {
  const { t } = useTranslation(); const [open, setOpen] = useState(false);
  return <div className="border-t border-[#ECEFF5]">
    <div
      className={`${rowClass} cursor-pointer hover:bg-[#F8F9FC] transition-colors select-none`}
      style={tableGridStyle}
      onClick={() => setOpen(prev => !prev)}
      role="button"
      tabIndex={0}
      aria-expanded={open}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          setOpen(prev => !prev);
        }
      }}
    >
      <div className="min-w-0 pl-2 pr-3 flex items-center gap-2">
        <button
          type="button"
          className="p-1 -ml-1 text-[#8A93B2] hover:text-[#1A162F] transition-colors rounded focus-visible:outline focus-visible:outline-2 shrink-0"
          aria-expanded={open}
          onClick={e => { e?.stopPropagation?.(); setOpen(prev => !prev); }}
        >
          <DisclosureIcon open={open} />
        </button>
        <div className="min-w-0">
          <div className="text-sm font-medium text-[#1A162F] truncate">{item.name}</div>
          <div className={muted}>
            {t('website.usage.tree.updateCount', { count: item.run_count })}
            {item.analysis_count > 0 && ` · ${t('website.usage.tree.analysisCount', { count: item.analysis_count })}`}
          </div>
        </div>
      </div>
      <div className="pl-5"><Time value={item.latest_at} /></div>
      <div className="min-w-0 pr-4 text-sm"><div className="truncate">{item.creator_name || item.creator_email || '—'}</div>
        {item.creator_name && item.creator_email && <div className={`${muted} truncate`}>{item.creator_email}</div>}</div>
      <div className="justify-self-start"><Amount item={item} pending={item.pending_count > 0} className="text-sm font-semibold tabular-nums text-[#1A162F]" /></div>
    </div>
    {open && <div className="overflow-hidden animate-in fade-in slide-in-from-top-1 duration-200 ease-out">
      <Runs monitor={item.id} period={period} />
    </div>}
  </div>;
}
export default function MarketUsageTree() {
  const { t } = useTranslation(); const [page, setPage] = useState(1);
  const { state, retry } = useUsagePage<MarketUsageMonitor>({ page });
  return <section id="usage-record-panel" role="tabpanel" aria-labelledby="usage-tab-market_intelligence"
    className="overflow-hidden rounded-[20px] border border-[#E8EBF3] bg-white text-[#1A162F] shadow-[0_10px_24px_rgba(26,22,47,0.06)]">
    <div className="overflow-x-auto"><div className="min-w-[840px]">
      <div className="border-b border-[#ECEFF5] bg-[#FCFCFC] px-6 py-3">
        <div className="grid items-center gap-0" style={tableGridStyle}>
          <div className="min-w-0 pl-2 pr-3 text-left text-xs font-medium text-[#8A93B2]">{t('website.usage.monitorName')}</div>
          <div className="pl-5 text-left text-xs font-medium text-[#8A93B2]">{t('website.usage.time')}</div>
          <div className="min-w-0 pr-4 text-left text-xs font-medium text-[#8A93B2]">{t('website.usage.user')}</div>
          <div className="justify-self-start text-left text-xs font-medium text-[#8A93B2] [font-variant-numeric:tabular-nums]">{t('website.usage.creditsChange')}</div>
        </div>
      </div>
      <LoadState state={state} retry={retry} />
      {state.status === 'ready' && (state.data.period ? state.data.items.map(item =>
        <MonitorRow key={`${state.data.period!.id}:${page}:${item.id}`} item={item} period={state.data.period!} />)
        : <p className="p-6 text-sm">{t('website.usage.tree.noPeriod')}</p>)}
    </div></div>
    {state.status === 'ready' && <TablePages page={page} total={state.data.total} setPage={setPage} />}
  </section>;
}
